import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './profile.scss';

type WorkStatus = 'played' | 'ready' | 'study';
type WorkType = '독주' | '실내악' | '협주' | '오케스트라';

interface Work {
  cat: string;
  title: string;
  subtitle?: string;
  sub: string;
  type: WorkType;
  status: WorkStatus;
  statusLabel: string;
}

interface Composer {
  name: string;
  life: string;
  works: Work[];
}

interface Gig {
  date: string;
  time: string;
  work: string;
  venue: string;
}

interface HistoryEvent {
  date: string;
  time: string;
  title: string;
  venue: string;
  role: string;
  work: string;
}

interface HistoryYear {
  year: string;
  count: string;
  events: HistoryEvent[];
}

const gigs: Gig[] = [
  { date: '11.14', time: 'SAT 19:30', work: '베토벤 바이올린 소나타 전곡 Ⅲ', venue: '금호아트홀 연세 · 피아노 김도현' },
  { date: '12.02', time: 'WED 20:00', work: '라벨 현악 4중주 F장조', venue: '예술의전당 IBK챔버홀 · 앙상블 오르페우스' },
  { date: '01.09', time: 'SAT 17:00', work: '시벨리우스 바이올린 협주곡', venue: '롯데콘서트홀 · 카메라타 서울' },
];

const filters: { label: string; type: WorkType | null; count: number }[] = [
  { label: '전체', type: null, count: 128 },
  { label: '독주', type: '독주', count: 46 },
  { label: '실내악', type: '실내악', count: 51 },
  { label: '협주', type: '협주', count: 19 },
  { label: '오케스트라', type: '오케스트라', count: 12 },
];

const composers: Composer[] = [
  {
    name: 'J. S. Bach', life: '1685 — 1750',
    works: [
      { cat: 'BWV 1001', title: 'Sonata No. 1 for Solo Violin in G minor', sub: '무반주 · 18분 · 2024.11 첫 무대', type: '독주', status: 'played', statusLabel: '연주' },
      { cat: 'BWV 1004', title: 'Partita No. 2 in D minor', subtitle: '— Chaconne', sub: '무반주 · 31분 · 2026.10 독주회 전곡', type: '독주', status: 'played', statusLabel: '연주' },
      { cat: 'BWV 1042', title: 'Violin Concerto in E major', sub: '협주 · 19분 · 오케스트라 2회', type: '협주', status: 'ready', statusLabel: '준비 중' },
    ],
  },
  {
    name: 'L. v. Beethoven', life: '1770 — 1827',
    works: [
      { cat: 'Op. 24', title: 'Violin Sonata No. 5 in F major', subtitle: '“Spring”', sub: '독주 · 24분 · 2026.11 전곡 시리즈 Ⅲ', type: '독주', status: 'ready', statusLabel: '이번 시즌' },
      { cat: 'Op. 61', title: 'Violin Concerto in D major', sub: '협주 · 42분 · 학습 중', type: '협주', status: 'study', statusLabel: '학습' },
    ],
  },
  {
    name: 'J. Brahms', life: '1833 — 1897',
    works: [
      { cat: 'Op. 78', title: 'Violin Sonata No. 1 in G major', sub: '독주 · 27분 · 2025.06 스튜디오 녹음', type: '독주', status: 'played', statusLabel: '연주' },
      { cat: 'Op. 108', title: 'Violin Sonata No. 3 in D minor', sub: '독주 · 22분 · 학습 중', type: '독주', status: 'study', statusLabel: '학습' },
    ],
  },
  {
    name: 'E. Ysaÿe', life: '1858 — 1931',
    works: [
      { cat: 'Op. 27/3', title: 'Sonata No. 3 in D minor', subtitle: '“Ballade”', sub: '무반주 · 8분 · 2025.11 앙코르', type: '독주', status: 'played', statusLabel: '연주' },
    ],
  },
];

const history: HistoryYear[] = [
  {
    year: '2026', count: '5회',
    events: [
      { date: '10.05', time: 'SAT 17:00', title: '독주회 “무반주, 그 이후”', venue: '금호아트홀 연세', role: '바이올린', work: 'Bach, Partita No. 2 BWV 1004 전곡' },
      { date: '06.21', time: 'SUN 19:30', title: '카메라타 서울 정기연주회', venue: '예술의전당 콘서트홀', role: '협연', work: 'Mozart, Violin Concerto No. 5 K. 219' },
      { date: '03.14', time: 'SAT 20:00', title: '트리오 베르데 창단 연주', venue: '세종문화회관 체임버홀', role: '제1바이올린', work: 'Brahms, Piano Trio No. 1 Op. 8' },
    ],
  },
  {
    year: '2025', count: '11회',
    events: [
      { date: '11.30', time: 'SUN 15:00', title: '청소년 협연의 밤', venue: '구리문화예술회관', role: '협연', work: 'Saint-Saëns, Introduction et Rondo capriccioso Op. 28' },
      { date: '08.09', time: 'SAT 18:00', title: '여름 실내악 축제', venue: '부산문화회관 챔버홀', role: '제2바이올린', work: 'Dvořák, String Quartet No. 12 Op. 96 “American”' },
    ],
  },
];

const media = [
  { title: '바흐 · 샤콘느 (라이브)', sub: '2026.10.05 금호아트홀 연세 · 무반주 파르티타 2번 중', time: '14:32' },
  { title: '브람스 · 소나타 1번 1악장', sub: '2025.06 스튜디오 녹음 · 피아노 김도현', time: '08:41' },
];

const facts = [
  { dt: '전공', dd: '바이올린 · 기악과' },
  { dt: '사사', dd: '정하윤, M. 로젠펠트' },
  { dt: '악기', dd: 'G. B. Guadagnini, 1782' },
  { dt: '언어', dd: '한국어 · 영어 · 독일어(읽기)' },
];

const availability: { label: string; badge: 'on' | 'mid' | 'off'; text: string }[] = [
  { label: '협연', badge: 'on', text: '가능' },
  { label: '실내악', badge: 'on', text: '가능' },
  { label: '리사이틀 게스트', badge: 'mid', text: '협의' },
  { label: '레슨', badge: 'off', text: '불가' },
];

const ensembles = [
  { name: '앙상블 오르페우스', role: '제1바이올린', since: '2024 —' },
  { name: '트리오 베르데', role: '바이올린', since: '2023 —' },
];

const subnavItems = [
  { id: 'repertoire', label: '레퍼토리' },
  { id: 'history', label: '연주 이력' },
  { id: 'media', label: '녹음·영상' },
  { id: 'about', label: '소개' },
  { id: 'scores', label: '악보 보관함' },
];

function BrandMark() {
  return (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" aria-hidden="true">
      <path d="M3 19c0-6.6 4.5-12 10-12s10 5.4 10 12" stroke="#0B0B0A" strokeWidth="1.2" />
      <path d="M13 7V3" stroke="#0B0B0A" strokeWidth="1.2" />
      <circle cx="13" cy="20.5" r="2.4" fill="#D9902A" />
    </svg>
  );
}

export default function Profile() {

  const [viewMode, setViewMode] = useState<'web' | 'app'>('web');
  const [activeFilter, setActiveFilter] = useState<WorkType | null>(null);
  const [activeSection, setActiveSection] = useState('repertoire');

  const filteredComposers = composers
    .map((c) => ({ ...c, works: c.works.filter((w) => !activeFilter || w.type === activeFilter) }))
    .filter((c) => c.works.length > 0);

  return (
    <div className="profile-page">
      <div className={`frame${viewMode === 'app' ? ' app' : ''}`}>

        {/* ---------------- top bar ---------------- */}
        <header className="topbar">
          <Link className="brand" to="/">
            <BrandMark />
            <span className="brand-txt">
              <span className="brand-kr">클래시커</span>
              <span className="brand-lat">CLASSICKER</span>
            </span>
          </Link>

          <label className="search">
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="4.6" stroke="#0B0B0A" strokeWidth="1.1" /><path d="M10.6 10.6L14 14" stroke="#0B0B0A" strokeWidth="1.1" /></svg>
            <input type="search" placeholder="음악가, 작품, 앙상블 검색" aria-label="검색" />
          </label>

          <div className="top-right">
            <div className="seg" role="group" aria-label="화면 보기">
              <button type="button" aria-pressed={viewMode === 'web'} onClick={() => setViewMode('web')}>웹</button>
              <button type="button" aria-pressed={viewMode === 'app'} onClick={() => setViewMode('app')}>앱</button>
            </div>
            <button className="icon-btn" type="button" aria-label="알림">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M4 6.5a4 4 0 118 0c0 3 1 4 1 4H3s1-1 1-4z" stroke="currentColor" strokeWidth="1.1" /><path d="M6.5 13a1.6 1.6 0 003 0" stroke="currentColor" strokeWidth="1.1" /></svg>
              <span className="dot"></span>
            </button>
            <div className="avatar">윤</div>
          </div>
        </header>

        {/* ---------------- hero ---------------- */}
        <section className="hero">
          <div>
            <p className="eyebrow">바이올린 · 앙상블 오르페우스 제1바이올린</p>
            <h1>윤서하</h1>
            <p className="name-lat">YOON&nbsp;&nbsp;SEOHA</p>
            <p className="tagline">바흐에서 베토벤까지 — 악보의 여백을 소리로 읽는 연주자.</p>
            <div className="meta">
              <span>한국예술종합학교 기악과 22학번</span>
              <span>서울</span>
              <span>프로필 활동 3년</span>
            </div>
            <div className="actions">
              <button className="btn btn-primary" type="button">
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M2 3.5h12v9H2z" stroke="currentColor" strokeWidth="1.1" /><path d="M2 4l6 4.5L14 4" stroke="currentColor" strokeWidth="1.1" /></svg>
                프로그램 보내기
              </button>
              <button className="btn btn-ghost" type="button">팔로우</button>
              <button className="btn btn-ghost btn-icon" type="button" aria-label="공유">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 2v8M5 5l3-3 3 3" stroke="currentColor" strokeWidth="1.1" /><path d="M3 9v4h10V9" stroke="currentColor" strokeWidth="1.1" /></svg>
              </button>
            </div>
          </div>

          <div className="season">
            <div className="season-head"><span>이번 시즌</span><b>2026 — 27</b></div>
            {gigs.map((gig) => (
              <div className="gig" key={gig.date}>
                <span className="gig-date">{gig.date}<em>{gig.time}</em></span>
                <span>
                  <span className="gig-work">{gig.work}</span>
                  <span className="gig-venue">{gig.venue}</span>
                </span>
              </div>
            ))}
            <a className="season-more" href="#history" onClick={() => setActiveSection('history')}>전체 일정 보기 →</a>
          </div>
        </section>

        {/* ---------------- sub nav ---------------- */}
        <nav className="subnav">
          {subnavItems.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              aria-current={activeSection === item.id ? 'page' : undefined}
              onClick={() => setActiveSection(item.id)}
            >
              {item.label}
            </a>
          ))}
          <span className="right"><span className="status-pill"><i></i>협연·실내악 가능</span></span>
        </nav>

        <div className="cols">
          <div>

            {/* ---------------- repertoire ---------------- */}
            <section className="block" id="repertoire">
              <div className="sec-head">
                <span className="sec-num">01</span>
                <h2>레퍼토리</h2>
                <span className="sec-count">128곡</span>
              </div>
              <p className="sec-desc">무대에서 연주한 곡과 지금 준비 중인 곡을 한곳에. 곡별 상태를 열어두면 프로그램을 짜는 사람이 묻지 않고도 답을 얻습니다.</p>

              <div className="filters">
                {filters.map((f) => (
                  <button
                    key={f.label}
                    className="chip"
                    type="button"
                    aria-pressed={activeFilter === f.type}
                    onClick={() => setActiveFilter(f.type)}
                  >
                    {f.label} {f.count}
                  </button>
                ))}
              </div>

              {filteredComposers.length === 0 && (
                <p className="empty">이 분류에 공개된 곡이 아직 없습니다.</p>
              )}

              {filteredComposers.map((composer) => (
                <div className="composer" key={composer.name}>
                  <div className="composer-head">
                    <span className="composer-name">{composer.name}</span>
                    <span className="composer-life">{composer.life}</span>
                    <span className="composer-n">{composer.works.length}곡</span>
                  </div>
                  {composer.works.map((work) => (
                    <div className="work" key={work.cat}>
                      <div className="work-cat">{work.cat}</div>
                      <div>
                        <div className="work-title">
                          {work.title}{work.subtitle && <> <i>{work.subtitle}</i></>}
                        </div>
                        <div className="work-sub">{work.sub}</div>
                      </div>
                      <div className="work-tags">
                        <span className={`tag t-${work.status}`}>{work.statusLabel}</span>
                        <button className="add" type="button" aria-label="프로그램에 담기">+</button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </section>

            {/* ---------------- history ---------------- */}
            <section className="block" id="history">
              <div className="sec-head">
                <span className="sec-num">02</span>
                <h2>연주 이력</h2>
                <span className="sec-count">42회</span>
              </div>
              <p className="sec-desc">날짜·장소·역할이 함께 남습니다. 앙상블 단위로도, 작품 단위로도 다시 꺼내 볼 수 있습니다.</p>

              <div className="tl">
                {history.map((y) => (
                  <React.Fragment key={y.year}>
                    <div className="tl-year">{y.year}<small>{y.count}</small></div>
                    {y.events.map((evt) => (
                      <div className="evt" key={`${y.year}-${evt.date}`}>
                        <div className="evt-date">{evt.date}<em>{evt.time}</em></div>
                        <div>
                          <div className="evt-title">{evt.title}</div>
                          <div className="evt-meta">{evt.venue} · <b>{evt.role}</b></div>
                          <span className="evt-work">{evt.work}</span>
                        </div>
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </section>

            {/* ---------------- media ---------------- */}
            <section className="block" id="media">
              <div className="sec-head">
                <span className="sec-num">03</span>
                <h2>녹음 · 영상</h2>
                <span className="sec-count">9개</span>
              </div>
              <p className="sec-desc">프로그램을 보내기 전에, 소리를 먼저 확인할 수 있게.</p>

              <div className="media-grid">
                {media.map((m) => (
                  <div className="media" key={m.title}>
                    <span className="play"><svg width="11" height="12" viewBox="0 0 11 12" fill="none"><path d="M1 1l9 5-9 5z" fill="currentColor" /></svg></span>
                    <div>
                      <div className="media-title">{m.title}</div>
                      <div className="media-sub">{m.sub}</div>
                    </div>
                    <span className="media-time">{m.time}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* ---------------- about ---------------- */}
            <section className="block" id="about">
              <div className="sec-head">
                <span className="sec-num">04</span>
                <h2>소개</h2>
              </div>
              <div className="about-text">
                <p>활을 켜기 전에 악보의 여백을 먼저 읽습니다. 빠르게 지나가는 악장보다, 멈춰 있는 화음이 무엇을 참고 있는지에 오래 머무는 편입니다.</p>
                <p>한국예술종합학교에서 바이올린을 전공하고 있으며, 앙상블 오르페우스와 트리오 베르데에서 실내악을 이어가고 있습니다. 협연과 리사이틀 프로그램 제안은 언제든 환영합니다.</p>
              </div>
              <dl className="facts">
                {facts.map((f) => (
                  <div key={f.dt}><dt>{f.dt}</dt><dd>{f.dd}</dd></div>
                ))}
              </dl>
            </section>

          </div>

          {/* ---------------- aside ---------------- */}
          <aside>
            <div className="card">
              <div className="card-title">지금 연주 가능 <b>2026.12 —</b></div>
              <div className="avail">
                {availability.map((a) => (
                  <div className="avail-row" key={a.label}>
                    <span>{a.label}</span>
                    <span className={`badge-${a.badge}`}>{a.text}</span>
                  </div>
                ))}
              </div>
              <p className="note">12월 이후 일정 조율 가능 · 서울 / 경기</p>
            </div>

            <div className="card">
              <div className="card-title">소속 앙상블</div>
              <div className="ens">
                {ensembles.map((e) => (
                  <div className="ens-item" key={e.name}>
                    <span className="ens-name">{e.name}</span>
                    <span className="ens-role">{e.role}<br />{e.since}</span>
                  </div>
                ))}
              </div>
              <div className="tag-mini">
                <span>현악 4중주</span><span>피아노 3중주</span><span>듀오</span>
              </div>
            </div>

            <div className="card">
              <div className="card-title">프로그램 요청</div>
              <button className="btn btn-primary" type="button">프로그램 보내기</button>
              <button className="btn btn-ghost" type="button">메시지</button>
              <p className="updated">3일 전 업데이트</p>
            </div>
          </aside>
        </div>

        <footer className="foot">
          <span><b>클래시커</b> — 클래식을 연주하고 듣는 사람들의 프로필</span>
          <span>이 페이지는 예시 프로필입니다 · 인물과 일정은 가상</span>
        </footer>

      </div>
    </div>
  );
}
